import { TPageSummary } from "domain/entities/page-summary-response";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { checkParamsCreativeOnly } from "helper/filtersHelper";

type TPageSummaryProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
  showCards?: boolean;
};

export type TPageSummaryState = {
  requestParamsIndex: number;
  pageSummaryData?: TPageSummary;
  downloadingReports?: boolean;
  downloadPageSummaryReport: () => void;
  isLoading: boolean;
};

const initContext: TPageSummaryState = {
  requestParamsIndex: 0,
  downloadPageSummaryReport: () => null,
  isLoading: true,
};
export const PageSummaryContext = createContext(initContext);

export const PageSummaryProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
  showCards = true,
}: TPageSummaryProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageSummaryData, setPageSummaryData] = useState<any>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadPageSummaryReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportPageSummaryCSV(currentRequestParams)
      .then(createBlobDownloader("page-summary.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsCreativeOnly);
    }

    return checkParamsCreativeOnly(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getPageSummary({ ...currentRequestParams, showCards })
        .then(setPageSummaryData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <PageSummaryContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        pageSummaryData,
        downloadPageSummaryReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </PageSummaryContext.Provider>
  );
};
